import { useEffect, useRef, useState } from 'react';

import usePageMetadata from '../../hooks/usePageMetadata';
import { calculateReamer } from '../../services/toleranceService';
import './ReamerCalculatorPage.css';
import { useUnits } from '../../units/UnitContext';
import { formatUnitValue, unitLabel } from '../../units/unitConversions';
import { useUnitDrafts, initialUnitDrafts } from '../../units/useUnitDrafts';

const PAGE_TITLE = 'Reamer Calculator for Solid Carbide Reamers | ASiJoLab';
const PAGE_DESCRIPTION =
  'Calculate solid carbide reamer manufacturing dimensions up to Ø20 mm from ISO tolerances, custom hole limits or general reamed-hole requirements, including coating compensation and pre-drill size.';
const PAGE_CANONICAL = 'https://asijolab.com/tolerances/reamer-calculator';

const trimNumber = (value, maximumDecimals = 6) => {
  const fixed = Number(value).toFixed(maximumDecimals);
  const [whole, fraction] = fixed.split('.');
  const trimmedFraction = fraction.replace(/0+$/, '');
  return trimmedFraction ? `${whole}.${trimmedFraction}` : whole;
};

export const formatDiameter = (value) => {
  const [whole, fraction = ''] = trimNumber(value).split('.');
  return `${whole}.${fraction.padEnd(3, '0')}`;
};

export const formatDeviation = (value) => {
  const number = Number(value);
  if (number === 0) return '0';
  return `${number > 0 ? '+' : ''}${trimNumber(number)}`;
};

const DiameterRange = ({ range, unitSystem = 'metric' }) => (
  <>
    {unitSystem === 'inch' ? formatUnitValue(range.minDiameterMm, 'mm', unitSystem) : formatDiameter(range.minDiameterMm)} – {unitSystem === 'inch' ? formatUnitValue(range.maxDiameterMm, 'mm', unitSystem) : formatDiameter(range.maxDiameterMm)} {unitLabel('mm', unitSystem)}
  </>
);

const DeviationRange = ({ range, unitSystem = 'metric' }) => (
  <>
    {unitSystem === 'inch' ? formatUnitValue(range.lowerDeviationUm, 'um', unitSystem, { signed: true }) : formatDeviation(range.lowerDeviationUm)} / {unitSystem === 'inch' ? formatUnitValue(range.upperDeviationUm, 'um', unitSystem, { signed: true }) : formatDeviation(range.upperDeviationUm)} {unitLabel('um', unitSystem)}
  </>
);

const ReamerResults = ({ result }) => {
  const { unitSystem } = useUnits();
  const dimension = value => unitSystem === 'inch' ? formatUnitValue(value, 'mm', unitSystem) : formatDiameter(value);
  const micro = value => unitSystem === 'inch' ? formatUnitValue(value, 'um', unitSystem) : trimNumber(value);
  const manufacturing = result.manufacturing;
  const defaultTolerance = manufacturing.defaultManufacturingToleranceUm
    ?? result.defaults.manufacturingToleranceUm;
  const appliedTolerance = manufacturing.appliedManufacturingToleranceUm
    ?? result.input.manufacturingToleranceUm;

  return (
    <article className="reamerResults" aria-labelledby="reamer-results-title">
      <h2 id="reamer-results-title">Reamer calculation</h2>

      <section className="reamerResultSection" aria-labelledby="reamer-hole-title">
        <h3 id="reamer-hole-title">Hole</h3>
        <dl>
          <div><dt>Nominal diameter</dt><dd>Ø{dimension(result.input.diameterMm)} {unitLabel('mm', unitSystem)}</dd></div>
          {result.hole ? (
            <>
              <div><dt>Required hole</dt><dd><DiameterRange range={result.hole} unitSystem={unitSystem} /></dd></div>
              <div><dt>Deviation</dt><dd><DeviationRange range={result.hole} unitSystem={unitSystem} /></dd></div>
              <div><dt>Tolerance width</dt><dd>{micro(result.hole.toleranceWidthUm)} {unitLabel('um', unitSystem)}</dd></div>
            </>
          ) : (
            <div><dt>Hole tolerance</dt><dd>No specific hole tolerance selected.</dd></div>
          )}
        </dl>
      </section>

      {result.recommendedReamerZone && (
        <section className="reamerResultSection" aria-labelledby="reamer-zone-title">
          <h3 id="reamer-zone-title">Recommended reamer zone</h3>
          <p className="reamerPrimaryValue"><DiameterRange range={result.recommendedReamerZone} unitSystem={unitSystem} /></p>
          <dl>
            <div><dt>Deviation</dt><dd><DeviationRange range={result.recommendedReamerZone} unitSystem={unitSystem} /></dd></div>
            <div><dt>Zone width</dt><dd>{micro(result.recommendedReamerZone.widthUm)} {unitLabel('um', unitSystem)}</dd></div>
          </dl>
          <p className="reamerSectionHelp">
            Recommended zone within the requested hole tolerance used to position
            the reamer manufacturing range.
          </p>
        </section>
      )}

      <section className="reamerResultSection" aria-labelledby="reamer-manufacturing-title">
        <h3 id="reamer-manufacturing-title">Manufacturing</h3>
        <dl>
          <div><dt>Catalogue default manufacturing tolerance</dt><dd>{micro(defaultTolerance)} {unitLabel('um', unitSystem)}</dd></div>
          <div><dt>Applied manufacturing tolerance</dt><dd>{micro(appliedTolerance)} {unitLabel('um', unitSystem)}</dd></div>
        </dl>

        {!manufacturing.manufacturable ? (
          <div className="reamerWarning" role="status">
            <strong>Manufacturing tolerance is too wide for the recommended reamer zone.</strong>
            <dl>
              <div><dt>Available recommended zone width</dt><dd>{micro(manufacturing.requiredZoneWidthUm)} {unitLabel('um', unitSystem)}</dd></div>
              <div><dt>Applied manufacturing tolerance</dt><dd>{micro(appliedTolerance)} {unitLabel('um', unitSystem)}</dd></div>
            </dl>
            <p>Use a more precise grinding tolerance or review the requested hole tolerance.</p>
          </div>
        ) : (
          <div className="reamerManufacturingGrid">
            <section aria-labelledby="reamer-uncoated-title">
              <h4 id="reamer-uncoated-title">Uncoated reamer</h4>
              <dl>
                <div><dt>Suggested manufacturing diameter</dt><dd><DiameterRange range={manufacturing.uncoated} unitSystem={unitSystem} /></dd></div>
                <div><dt>Deviation</dt><dd><DeviationRange range={manufacturing.uncoated} unitSystem={unitSystem} /></dd></div>
              </dl>
            </section>

            <section aria-labelledby="reamer-coated-title">
              <h4 id="reamer-coated-title">Coated reamer</h4>
              <dl>
                <div><dt>Coating thickness on diameter</dt><dd>{micro(manufacturing.coated.coatingThicknessDiameterUm)} {unitLabel('um', unitSystem)}</dd></div>
                <div><dt>Manufacture before coating</dt><dd><DiameterRange range={manufacturing.coated.beforeCoating} unitSystem={unitSystem} /></dd></div>
                <div><dt>Deviation before coating</dt><dd><DeviationRange range={manufacturing.coated.beforeCoating} unitSystem={unitSystem} /></dd></div>
                <div><dt>Expected after coating</dt><dd><DiameterRange range={manufacturing.coated.expectedAfterCoating} unitSystem={unitSystem} /></dd></div>
              </dl>
            </section>
          </div>
        )}
      </section>

      {result.predrill && (
        <section className="reamerResultSection" aria-labelledby="reamer-predrill-title">
          <h3 id="reamer-predrill-title">Pre-drill</h3>
          <dl>
            <div><dt>Approx. recommended pre-drill</dt><dd>Ø{dimension(result.predrill.diameterMm)} {unitLabel('mm', unitSystem)}</dd></div>
            <div><dt>Stock for reaming</dt><dd>{dimension(result.predrill.stockForReamingOnDiameterMm)} {unitLabel('mm', unitSystem)} on diameter</dd></div>
          </dl>
          <p className="reamerSectionHelp">
            Approximate starting recommendation. The optimal pre-drill may vary
            depending on workpiece material, hole depth, machine rigidity and machining conditions.
          </p>
        </section>
      )}
    </article>
  );
};

const ReamerCalculatorPage = () => {
  const { unitSystem } = useUnits();
  const fields = { diameterMm: 'mm', lowerDeviationUm: 'um', upperDeviationUm: 'um', manufacturingToleranceUm: 'um', coatingThicknessDiameterUm: 'um' };
  const [mode, setMode] = useState('iso');
  const [form, setForm] = useState(() => initialUnitDrafts({
    diameterMm: '',
    tolerance: '',
    lowerDeviationUm: '',
    upperDeviationUm: '',
    manufacturingToleranceUm: '',
    coatingThicknessDiameterUm: '4.0',
  }, fields, unitSystem));
  const canonicalInput = useUnitDrafts(form, setForm, fields, unitSystem);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const requestRef = useRef(null);

  usePageMetadata(PAGE_TITLE, PAGE_DESCRIPTION, {
    canonical: PAGE_CANONICAL,
    robots: 'index, follow',
  });

  useEffect(() => () => requestRef.current?.abort(), []);

  const clearCalculation = () => {
    requestRef.current?.abort();
    requestRef.current = null;
    setLoading(false);
    setResult(null);
    setError('');
  };

  const updateField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    clearCalculation();
  };

  const updateIsoTolerance = (event) => {
    setForm((current) => ({ ...current, tolerance: event.target.value.toUpperCase() }));
    clearCalculation();
  };

  const changeMode = (event) => {
    const nextMode = event.target.value;
    setMode(nextMode);
    setForm((current) => ({
      ...current,
      tolerance: nextMode === 'iso' ? current.tolerance : '',
      lowerDeviationUm: nextMode === 'custom' ? current.lowerDeviationUm : '',
      upperDeviationUm: nextMode === 'custom' ? current.upperDeviationUm : '',
    }));
    clearCalculation();
  };

  const validate = () => {
    if (form.diameterMm === '') return 'Enter a hole diameter.';
    const diameterMm = canonicalInput('diameterMm', form.diameterMm, 'mm');
    if (!Number.isFinite(diameterMm) || diameterMm < 0.1 || diameterMm > 20) {
      return unitSystem === 'inch' ? `Hole diameter must be between ${formatUnitValue(0.1, 'mm', unitSystem)} and ${formatUnitValue(20, 'mm', unitSystem)} in.` : 'Hole diameter must be between 0.10 and 20.00 mm.';
    }
    if (mode === 'iso' && form.tolerance.trim() === '') {
      return 'Enter an ISO hole tolerance designation.';
    }
    if (mode === 'custom') {
      if (form.lowerDeviationUm === '' || form.upperDeviationUm === '') {
        return 'Enter both custom hole deviations.';
      }
      const lower = Number(form.lowerDeviationUm);
      const upper = Number(form.upperDeviationUm);
      if (!Number.isFinite(lower) || !Number.isFinite(upper)) {
        return 'Custom deviations must be valid numbers.';
      }
      if (upper <= lower) return 'Upper deviation must be greater than lower deviation.';
    }
    if (form.manufacturingToleranceUm !== '') {
      const manufacturing = Number(form.manufacturingToleranceUm);
      if (!Number.isFinite(manufacturing) || manufacturing <= 0) {
        return 'Manufacturing tolerance must be greater than zero.';
      }
    }
    if (form.coatingThicknessDiameterUm !== '') {
      const coating = Number(form.coatingThicknessDiameterUm);
      if (!Number.isFinite(coating) || coating < 0) {
        return 'Coating thickness must be zero or greater.';
      }
    }
    return '';
  };

  const buildPayload = () => ({
    mode,
    diameterMm: canonicalInput('diameterMm', form.diameterMm, 'mm'),
    ...(mode === 'iso' && { tolerance: form.tolerance.trim() }),
    ...(mode === 'custom' && {
      lowerDeviationUm: canonicalInput('lowerDeviationUm', form.lowerDeviationUm, 'um'),
      upperDeviationUm: canonicalInput('upperDeviationUm', form.upperDeviationUm, 'um'),
    }),
    ...(form.manufacturingToleranceUm !== '' && {
      manufacturingToleranceUm: canonicalInput('manufacturingToleranceUm', form.manufacturingToleranceUm, 'um'),
    }),
    ...(form.coatingThicknessDiameterUm !== '' && {
      coatingThicknessDiameterUm: canonicalInput('coatingThicknessDiameterUm', form.coatingThicknessDiameterUm, 'um'),
    }),
  });

  const submit = async (event) => {
    event.preventDefault();
    const validationError = validate();
    if (validationError) {
      setResult(null);
      setError(validationError);
      return;
    }

    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setLoading(true);
    setResult(null);
    setError('');
    try {
      const nextResult = await calculateReamer(buildPayload(), { signal: controller.signal });
      if (requestRef.current !== controller) return;
      setResult(nextResult);
    } catch (requestError) {
      if (requestError?.code === 'ERR_CANCELED' || requestRef.current !== controller) return;
      setError(
        requestError?.response?.data?.message
        || 'The reamer calculation could not be completed. Please try again.'
      );
    } finally {
      if (requestRef.current === controller) {
        requestRef.current = null;
        setLoading(false);
      }
    }
  };

  return (
    <main className="reamerPage">
      <header className="reamerHeader">
        <p>Tolerances · Solid carbide reamers</p>
        <h1>Reamer Calculator</h1>
        <span>
          Calculate recommended manufacturing dimensions for solid carbide reamers
          up to Ø{formatUnitValue(20, 'mm', unitSystem)} {unitLabel('mm', unitSystem)} from an ISO hole tolerance, custom hole limits, or a general
          reamed-hole requirement.
        </span>
      </header>

      <p className="reamerIntro">
        Results include the recommended reamer zone, manufacturing tolerance,
        uncoated and coated manufacturing dimensions, and approximate pre-drill diameter.
      </p>

      <form className="reamerForm" onSubmit={submit} noValidate>
        <div className="reamerField">
          <label htmlFor="reamer-diameter">Hole diameter D [{unitLabel('mm', unitSystem)}]</label>
          <input id="reamer-diameter" type="number" min={formatUnitValue(0.1, 'mm', unitSystem, { input: true })} max={formatUnitValue(20, 'mm', unitSystem, { input: true })} step={unitSystem === 'inch' ? 'any' : '0.001'}
            value={form.diameterMm} onChange={updateField('diameterMm')} inputMode="decimal" />
          <small>Allowed range: {formatUnitValue(0.1, 'mm', unitSystem)}–{formatUnitValue(20, 'mm', unitSystem)} {unitLabel('mm', unitSystem)}.</small>
        </div>

        <fieldset className="reamerModes">
          <legend>Hole requirement</legend>
          <div>
            <label><input type="radio" name="reamer-mode" value="iso" checked={mode === 'iso'} onChange={changeMode} /> <span>ISO tolerance</span></label>
            <label><input type="radio" name="reamer-mode" value="custom" checked={mode === 'custom'} onChange={changeMode} /> <span>Custom limits</span></label>
            <label><input type="radio" name="reamer-mode" value="general" checked={mode === 'general'} onChange={changeMode} /> <span>No specified tolerance</span></label>
          </div>
        </fieldset>

        {mode === 'iso' && (
          <div className="reamerField">
            <label htmlFor="reamer-tolerance">ISO hole tolerance</label>
            <input id="reamer-tolerance" type="text" placeholder="H7 or N7"
              value={form.tolerance} onChange={updateIsoTolerance}
              autoComplete="off" spellCheck="false" />
            <small>Lowercase letters are converted to uppercase automatically.</small>
          </div>
        )}

        {mode === 'custom' && (
          <div className="reamerFieldGrid">
            <div className="reamerField">
              <label htmlFor="reamer-lower">Lower deviation [{unitLabel('um', unitSystem)}]</label>
              <input id="reamer-lower" type="number" step={unitSystem === 'inch' ? 'any' : '0.1'} placeholder={unitSystem === 'inch' ? '+0.000394' : '+10'}
                value={form.lowerDeviationUm} onChange={updateField('lowerDeviationUm')} inputMode="decimal" />
            </div>
            <div className="reamerField">
              <label htmlFor="reamer-upper">Upper deviation [{unitLabel('um', unitSystem)}]</label>
              <input id="reamer-upper" type="number" step={unitSystem === 'inch' ? 'any' : '0.1'} placeholder={unitSystem === 'inch' ? '+0.000827' : '+21'}
                value={form.upperDeviationUm} onChange={updateField('upperDeviationUm')} inputMode="decimal" />
            </div>
          </div>
        )}

        {mode === 'general' && (
          <p className="reamerModeHelp">
            Use this when no specific hole tolerance is required and you only need
            a practical manufacturing range for the reamer.
          </p>
        )}

        <div className="reamerFieldGrid">
          <div className="reamerField">
            <label htmlFor="reamer-manufacturing">Manufacturing tolerance [{unitLabel('um', unitSystem)}]</label>
            <input id="reamer-manufacturing" type="number" min="0" step={unitSystem === 'inch' ? 'any' : '0.1'}
              placeholder="Optional override" value={form.manufacturingToleranceUm}
              onChange={updateField('manufacturingToleranceUm')} inputMode="decimal" />
            <small>Leave blank to use the catalogue default: {formatUnitValue(3, 'um', unitSystem)} {unitLabel('um', unitSystem)} up to Ø{formatUnitValue(3, 'mm', unitSystem)} {unitLabel('mm', unitSystem)}, {formatUnitValue(4, 'um', unitSystem)} {unitLabel('um', unitSystem)} up to Ø{formatUnitValue(6, 'mm', unitSystem)} {unitLabel('mm', unitSystem)}, {formatUnitValue(5, 'um', unitSystem)} {unitLabel('um', unitSystem)} up to Ø{formatUnitValue(20, 'mm', unitSystem)} {unitLabel('mm', unitSystem)}.</small>
          </div>
          <div className="reamerField">
            <label htmlFor="reamer-coating">Coating thickness on tool diameter [{unitLabel('um', unitSystem)}]</label>
            <input id="reamer-coating" type="number" min="0" step={unitSystem === 'inch' ? 'any' : '0.1'}
              value={form.coatingThicknessDiameterUm}
              onChange={updateField('coatingThicknessDiameterUm')} inputMode="decimal" />
            <small>Total increase in tool diameter, not thickness per side. Actual coating thickness has its own manufacturing tolerance, which is not included in this calculation.</small>
          </div>
        </div>

        {error && <p className="reamerError" role="alert">{error}</p>}
        <button className="reamerSubmit" type="submit" disabled={loading}>
          {loading ? 'Calculating…' : 'Calculate reamer'}
        </button>
      </form>

      <div className="reamerResultArea" aria-live="polite" aria-busy={loading}>
        {result && <ReamerResults result={result} />}
      </div>
    </main>
  );
};

export default ReamerCalculatorPage;
